import React, { useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, Search, Download } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const AdminWaivers = () => {
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/admin/waivers", { withCredentials: true });
      setWaivers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load waivers");
    } finally {
      setLoading(false);
    }
  })(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return waivers;
    return waivers.filter((w) =>
      [w.name, w.email, w.phone, w.waiver_signature_name, w.waiver_parent_guardian_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [q, waivers]);

  return (
    <div className="space-y-5" data-testid="admin-waivers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-orbitron text-2xl font-black uppercase text-spike-text">Signed Waivers</h1>
          <p className="text-sm text-spike-text-muted mt-1">All users who have signed the liability waiver, newest first.</p>
        </div>
        <div className="text-sm text-spike-dim" data-testid="admin-waivers-count">Total: <span className="text-spike-blue-light font-bold">{waivers.length}</span></div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-spike-dim" />
        <input
          type="search"
          data-testid="admin-waivers-search"
          placeholder="Search by name, email or phone..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full bg-black/60 border border-white/15 rounded-md pl-10 pr-3 py-2 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-spike-dim p-6"><Loader2 className="w-4 h-4 animate-spin"/> Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-spike-elevated border border-white/10 rounded-lg p-10 text-center text-spike-text-muted">
          <ShieldCheck className="w-8 h-8 mx-auto text-spike-dim mb-2" />
          {waivers.length === 0 ? "No waivers signed yet." : "No matches for your search."}
        </div>
      ) : (
        <div className="overflow-x-auto bg-spike-elevated border border-white/10 rounded-lg">
          <table className="w-full text-sm" data-testid="admin-waivers-table">
            <thead className="bg-black/40 text-spike-dim text-xs font-orbitron uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Signed at</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">DOB</th>
                <th className="text-left px-4 py-3">Age</th>
                <th className="text-left px-4 py-3">Minor</th>
                <th className="text-left px-4 py-3">Guardian</th>
                <th className="text-left px-4 py-3">Sig</th>
                <th className="text-left px-4 py-3">PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((w) => (
                <tr key={w.user_id} className="hover:bg-black/40" data-testid={`waiver-row-${w.user_id}`}>
                  <td className="px-4 py-3 text-spike-text whitespace-nowrap">{fmt(w.waiver_signed_at)}</td>
                  <td className="px-4 py-3 text-spike-text">{w.waiver_signature_name || w.name || "—"}</td>
                  <td className="px-4 py-3 text-spike-text-muted">{w.email}</td>
                  <td className="px-4 py-3 text-spike-text-muted">{w.phone || "—"}</td>
                  <td className="px-4 py-3 text-spike-text-muted">{w.waiver_date_of_birth || "—"}</td>
                  <td className="px-4 py-3 text-spike-text-muted">{w.waiver_age_at_signing ?? "—"}</td>
                  <td className="px-4 py-3">{w.waiver_is_minor_at_signing ? <span className="text-amber-400 font-bold">Yes</span> : <span className="text-spike-dim">No</span>}</td>
                  <td className="px-4 py-3 text-spike-text-muted">{w.waiver_parent_guardian_name || "—"}</td>
                  <td className="px-4 py-3 text-spike-dim">{w.signature_type ? <span className="text-[10px] font-orbitron uppercase tracking-[0.15em]">{w.signature_type}</span> : "—"}</td>
                  <td className="px-4 py-3">
                    {w.latest_waiver_id ? (
                      <a
                        href={`${API_BASE}/api/admin/waivers/${w.latest_waiver_id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`waiver-download-${w.user_id}`}
                        className="inline-flex items-center gap-1 text-xs text-spike-blue-light hover:text-spike-blue"
                      >
                        <Download className="w-3 h-3" /> PDF
                      </a>
                    ) : (
                      <span className="text-xs text-spike-dim" title="Signed before PDF capture rolled out — needs re-sign">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminWaivers;
